import { RECUR_PRODUCT_NAME, ROCKET_SUMMON_ENTRY, ROCKET_SUMMON_HANDOFF, SPARK } from './constants/commonConstants';
import { isEmpty } from './utils';
import type { SummonUserData } from './utils';

/**
 * No fallback by design: the summon handoff must fail closed when this env var is unset (e.g. on
 * dev/staging, where it's deliberately left out), rather than defaulting to a hardcoded prod URL.
 */
export function getRocketUrl(): string | undefined {
    return process.env.REACT_APP_ROCKET_URL || undefined;
}

/**
 * ROCKET-summon-only gate: a borrower must be on the SPARK program to be summoned to
 * rocket.recur.club. `customFields.program` is a multi-valued array on the AICA org
 * (e.g. `['SWIFT', 'SPARK']`); an absent program is treated as non-SPARK, matching the
 * backend's reject-on-absent behaviour.
 */
export function isSparkCompany(company: { customFields?: { program?: string[] | string } }): boolean {
    const program = company?.customFields?.program;
    return Array.isArray(program) ? program.includes(SPARK) : program === SPARK;
}

/**
 * Shared by both "View Account" call-sites (header CTA + companies list row): the existing
 * summon action redirects to rocket.recur.club only when the PNO-config flag is explicitly on
 * for a Recur PNO with a configured rocket URL — mirrors `shouldRedirectPartner` in
 * `partnerRedirect.ts`. Callers must additionally gate on `isSparkCompany` (per-company, so it
 * isn't folded in here).
 */
export function shouldRedirectToRocket({
    productName,
    isRocketRedirectEnabled,
}: {
    productName?: string;
    isRocketRedirectEnabled: boolean;
}): boolean {
    return productName === RECUR_PRODUCT_NAME && isRocketRedirectEnabled === true && !!getRocketUrl();
}

/**
 * Non-secret ids go in the query string — `userId`/`platformUserId` identify the summoned
 * borrower session. The requester's identity is already durably recorded server-side (aica
 * SummonAudit), so it isn't repeated here — the rocket consumer never reads it, and an email in
 * a cross-domain URL would contradict this handoff's secrets-off-the-URL posture. The summon
 * token goes in the URL fragment so it never reaches server logs.
 * URLSearchParams URI-encodes every value on `.toString()`, so callers must not pre-encode.
 */
export function buildRocketSummonUrl(rocketUrl: string, userData: SummonUserData): string {
    const params = new URLSearchParams({
        [ROCKET_SUMMON_HANDOFF.USER_ID]: userData.userId ?? '',
        [ROCKET_SUMMON_HANDOFF.PLATFORM_USER_ID]: userData.platformUserId ?? '',
        [ROCKET_SUMMON_HANDOFF.ORG_ID]: userData.orgId ?? '',
        [ROCKET_SUMMON_HANDOFF.PARENT_ORG_ID]: userData.parentOrgId ?? '',
        [ROCKET_SUMMON_HANDOFF.ENTRY]: ROCKET_SUMMON_ENTRY,
    });
    const fragment = new URLSearchParams({
        [ROCKET_SUMMON_HANDOFF.SUMMON_TOKEN]: userData.authToken ?? '',
    });
    return `${rocketUrl}?${params.toString()}#${fragment.toString()}`;
}

/**
 * Shared by both "View Account" call-sites: opens the summoned borrower's rocket.recur.club
 * session in a new tab. Callers must already have gated on `shouldRedirectToRocket`, which is why
 * `rocketUrl` is only ever undefined in practice for a misuse the type system can't otherwise catch.
 * Returns `false` without opening a tab if `rocketUrl` or the summon token is missing, so a summon
 * response that reaches `onSuccess` without a usable token doesn't open a dead rocket session —
 * callers must surface that to the user.
 */
export function openRocketSummonTab(rocketUrl: string | undefined, userData: SummonUserData): boolean {
    if (isEmpty(rocketUrl) || isEmpty(userData.authToken)) return false;
    window.open(buildRocketSummonUrl(rocketUrl as string, userData), '_blank', 'noopener,noreferrer');
    return true;
}
